import { Trans, useLingui } from "@lingui/react/macro";
import {
  activityKeys,
  updateEndDate,
  type IRecurringActivitySchedule,
} from "@/api/Activities";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { Loading } from "@medoma/components/Loading/Loading";
import { Text } from "@medoma/components/Text/Text";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import {
  calendarDateSchema,
  humanFormat,
  machineFormat,
} from "@medoma/models/date-and-time";
import { endOfDay } from "date-fns";

type IEditEndDateFields = {
  endDate: string;
};

export const EditActivityEndDate = ({
  onSuccess,
  schedule,
  activityId,
}: {
  onSuccess: () => void;
  schedule: IRecurringActivitySchedule;
  activityId: string;
}) => {
  const { t } = useLingui();
  const methods = useForm<IEditEndDateFields>({
    defaultValues: {
      endDate: schedule.end
        ? machineFormat(schedule.end, "yyyy-MM-dd")
        : undefined,
    },
  });

  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    setError,
  } = methods;

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (endDateTime: Date) => updateEndDate(activityId, endDateTime),
    onError: (error) => {
      setError("endDate", {
        message: deducedError(error),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: activityKeys.all });
      onSuccess();
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    const { endDate } = formData;
    const endOfDayEndDateTime = endOfDay(calendarDateSchema.decode(endDate));
    mutate(endOfDayEndDateTime);
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {isPending ? (
          <Loading message={t`Ändrar slutdatum`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : null}
            <Form.Row>
              <div>
                <Text element="div" weight="medium">{t`Startdatum`}</Text>
                <Text element="div">
                  {humanFormat(schedule.start, {
                    month: "long",
                    day: "numeric",
                  })}
                </Text>
              </div>
            </Form.Row>
            <Form.Row>
              <InputField
                label={t`Slutdatum`}
                description={t`Aktivitetsserien fortsätter fram till och med det slutdatum du anger här`}
                showOptionalLabel={false}
                errorMessage={errors.endDate?.message}
                type="date"
                {...register(`endDate`, {
                  required: {
                    value: true,
                    message: t`Ange slutdatum`,
                  },
                  min: {
                    value: machineFormat(new Date(), "yyyy-MM-dd"),
                    message: t`Slutdatumet behöver vara idag eller senare`,
                  },
                })}
              />
            </Form.Row>
            <FilledButton type="submit">
              <Trans>Spara ändringar</Trans>
            </FilledButton>
          </>
        )}
      </Form>
    </FormProvider>
  );
};
